import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Rock, Issue, ActionItem, Metric, WinExcited, User, COLLECTION_NAMES } from '../types';
import { isDateInRange, getPreviousWeekRange, calculatePercentageChange } from './weeklySummary.util';

interface WeeklySummaryData {
  rocksCreated: number;
  rocksCompleted: number;
  rocksDropped: number;
  issuesCreated: number;
  issuesResolved: number;
  actionItemsCreated: number;
  actionItemsCompleted: number;
  metricsCreated: number;
  winsExcitedCount: number;
}

interface TrendData {
  current: WeeklySummaryData;
  previous: WeeklySummaryData;
  percentageChanges: {
    rocksCreated: number;
    rocksCompleted: number;
    issuesCreated: number;
    issuesResolved: number;
    actionItemsCreated: number;
    actionItemsCompleted: number;
  };
  actionItemCompletionRate: number;
}

/**
 * Hook for Weekly Summary data with Firestore
 * Aggregates data from multiple collections for a given date range
 */
export function useWeeklySummary(
  userId: string | undefined,
  startDate: Date,
  endDate: Date,
  selectedUserId?: string
) {
  const [rocks, setRocks] = useState<Rock[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [winsExcited, setWinsExcited] = useState<WinExcited[]>([]);
  const { eosUsers = [] } = useAuth();
  const [loading, setLoading] = useState(true);

  // Load rocks
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const rocksQuery = query(
      collection(db, COLLECTION_NAMES.ROCKS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      rocksQuery,
      (snapshot) => {
        const rocksData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            achievedAt: data.achievedAt?.toDate?.() || data.achievedAt,
            droppedAt: data.droppedAt?.toDate?.() || data.droppedAt,
          } as Rock;
        });
        setRocks(rocksData);
      },
      (error) => {
        console.error('Error loading rocks:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load issues
  useEffect(() => {
    if (!userId) {
      return;
    }

    const issuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      issuesQuery,
      (snapshot) => {
        const issuesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            firstOpenedDate: data.firstOpenedDate || data.createdAt,
          } as Issue;
        });
        setIssues(issuesData);
      },
      (error) => {
        console.error('Error loading issues:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load action items
  useEffect(() => {
    if (!userId) {
      return;
    }

    const actionItemsQuery = query(
      collection(db, COLLECTION_NAMES.ACTION_ITEMS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      actionItemsQuery,
      (snapshot) => {
        const actionItemsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
            completedDate: data.completedDate?.toDate?.() || data.completedDate,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
          } as ActionItem;
        });
        setActionItems(actionItemsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading action items:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load metrics
  useEffect(() => {
    if (!userId) {
      return;
    }

    const metricsQuery = query(
      collection(db, COLLECTION_NAMES.METRICS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      metricsQuery,
      (snapshot) => {
        const metricsData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            if (data.deletedAt) return null;
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as Metric;
          })
          .filter((metric) => metric !== null) as Metric[];
        setMetrics(metricsData);
      },
      (error) => {
        console.error('Error loading metrics:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load wins/excited
  useEffect(() => {
    if (!userId) {
      return;
    }

    const winsExcitedQuery = query(
      collection(db, COLLECTION_NAMES.WINS_EXCITED),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      winsExcitedQuery,
      (snapshot) => {
        const winsExcitedData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
          } as WinExcited;
        });
        setWinsExcited(winsExcitedData);
      },
      (error) => {
        console.error('Error loading wins/excited:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Filter data by user if specified
  const filterByUser = <T extends { ownerId?: string; userId?: string; createdBy?: string }>(
    items: T[]
  ): T[] => {
    if (!selectedUserId) return items;
    return items.filter((item) => 
      item.ownerId === selectedUserId || 
      item.userId === selectedUserId || 
      item.createdBy === selectedUserId
    );
  };

  // Convert Firestore Timestamp to Date
  const toDate = (timestamp: Timestamp | Date | string | undefined): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    return new Date(timestamp);
  };

  // Calculate summary for date range
  const getSummary = useMemo((): WeeklySummaryData => {
    const filteredRocks = filterByUser(rocks);
    const filteredIssues = filterByUser(issues);
    const filteredActionItems = filterByUser(actionItems);
    const filteredMetrics = filterByUser(metrics);
    const filteredWinsExcited = filterByUser(winsExcited);

    return {
      rocksCreated: filteredRocks.filter((rock) => {
        const created = toDate(rock.createdAt);
        return isDateInRange(created, startDate, endDate);
      }).length,
      rocksCompleted: filteredRocks.filter((rock) => {
        if (!rock.isAchieved || !rock.achievedAt) return false;
        const achieved = toDate(rock.achievedAt);
        return isDateInRange(achieved, startDate, endDate);
      }).length,
      rocksDropped: filteredRocks.filter((rock) => {
        if (!rock.isDropped || !rock.droppedAt) return false;
        const dropped = toDate(rock.droppedAt);
        return isDateInRange(dropped, startDate, endDate);
      }).length,
      issuesCreated: filteredIssues.filter((issue) => {
        const created = toDate(issue.createdAt);
        return isDateInRange(created, startDate, endDate);
      }).length,
      issuesResolved: filteredIssues.filter((issue) => {
        if (issue.status !== 'solved' || !issue.solvedAt) return false;
        const solved = toDate(issue.solvedAt);
        return isDateInRange(solved, startDate, endDate);
      }).length,
      actionItemsCreated: filteredActionItems.filter((actionItem) => {
        const created = toDate(actionItem.createdAt);
        return isDateInRange(created, startDate, endDate);
      }).length,
      actionItemsCompleted: filteredActionItems.filter((actionItem) => {
        if (!actionItem.complete || !actionItem.completedDate) return false;
        const completed = toDate(actionItem.completedDate);
        return isDateInRange(completed, startDate, endDate);
      }).length,
      metricsCreated: filteredMetrics.filter((metric) => {
        const created = toDate(metric.createdAt);
        return isDateInRange(created, startDate, endDate);
      }).length,
      winsExcitedCount: filteredWinsExcited.filter((entry) => {
        const created = toDate(entry.createdAt);
        return isDateInRange(created, startDate, endDate);
      }).length,
    };
  }, [rocks, issues, actionItems, metrics, winsExcited, startDate, endDate, selectedUserId]);

  // Calculate trend data (current vs previous week)
  const getTrendData = useMemo((): TrendData | null => {
    const previousWeekRange = getPreviousWeekRange(endDate);
    
    // Calculate previous week summary
    const previousSummary: WeeklySummaryData = {
      rocksCreated: filterByUser(rocks).filter((rock) => {
        const created = toDate(rock.createdAt);
        return isDateInRange(created, previousWeekRange.startDate, previousWeekRange.endDate);
      }).length,
      rocksCompleted: filterByUser(rocks).filter((rock) => {
        if (!rock.isAchieved || !rock.achievedAt) return false;
        const achieved = toDate(rock.achievedAt);
        return isDateInRange(achieved, previousWeekRange.startDate, previousWeekRange.endDate);
      }).length,
      rocksDropped: 0, // Not needed for trends
      issuesCreated: filterByUser(issues).filter((issue) => {
        const created = toDate(issue.createdAt);
        return isDateInRange(created, previousWeekRange.startDate, previousWeekRange.endDate);
      }).length,
      issuesResolved: filterByUser(issues).filter((issue) => {
        if (issue.status !== 'solved' || !issue.solvedAt) return false;
        const solved = toDate(issue.solvedAt);
        return isDateInRange(solved, previousWeekRange.startDate, previousWeekRange.endDate);
      }).length,
      actionItemsCreated: filterByUser(actionItems).filter((actionItem) => {
        const created = toDate(actionItem.createdAt);
        return isDateInRange(created, previousWeekRange.startDate, previousWeekRange.endDate);
      }).length,
      actionItemsCompleted: filterByUser(actionItems).filter((actionItem) => {
        if (!actionItem.complete || !actionItem.completedDate) return false;
        const completed = toDate(actionItem.completedDate);
        return isDateInRange(completed, previousWeekRange.startDate, previousWeekRange.endDate);
      }).length,
      metricsCreated: 0, // Not needed for trends
      winsExcitedCount: 0, // Not needed for trends
    };

    const actionItemCompletionRate = getSummary.actionItemsCreated > 0
      ? Math.round((getSummary.actionItemsCompleted / getSummary.actionItemsCreated) * 100)
      : 0;

    return {
      current: getSummary,
      previous: previousSummary,
      percentageChanges: {
        rocksCreated: calculatePercentageChange(getSummary.rocksCreated, previousSummary.rocksCreated),
        rocksCompleted: calculatePercentageChange(getSummary.rocksCompleted, previousSummary.rocksCompleted),
        issuesCreated: calculatePercentageChange(getSummary.issuesCreated, previousSummary.issuesCreated),
        issuesResolved: calculatePercentageChange(getSummary.issuesResolved, previousSummary.issuesResolved),
        actionItemsCreated: calculatePercentageChange(getSummary.actionItemsCreated, previousSummary.actionItemsCreated),
        actionItemsCompleted: calculatePercentageChange(getSummary.actionItemsCompleted, previousSummary.actionItemsCompleted),
      },
      actionItemCompletionRate,
    };
  }, [getSummary, rocks, issues, actionItems, startDate, endDate, selectedUserId]);

  // Get active rocks for the period
  const getActiveRocks = useMemo(() => {
    return filterByUser(rocks).filter((rock) => {
      if (rock.isAchieved || rock.isDropped) return false;
      const created = toDate(rock.createdAt);
      // Rock is active if created before or during the period
      return created <= endDate;
    });
  }, [rocks, endDate, selectedUserId]);

  // Get completed items
  const getCompletedItems = useMemo(() => {
    const completedActionItems = filterByUser(actionItems).filter((actionItem) => {
      if (!actionItem.complete || !actionItem.completedDate) return false;
      const completed = toDate(actionItem.completedDate);
      return isDateInRange(completed, startDate, endDate);
    });

    const resolvedIssues = filterByUser(issues).filter((issue) => {
      if (issue.status !== 'solved' || !issue.solvedAt) return false;
      const solved = toDate(issue.solvedAt);
      return isDateInRange(solved, startDate, endDate);
    });

    return { actionItems: completedActionItems, issues: resolvedIssues };
  }, [actionItems, issues, startDate, endDate, selectedUserId]);

  // Get wins/excited entries
  const getWinsExcitedEntries = useMemo(() => {
    return filterByUser(winsExcited).filter((entry) => {
      const created = toDate(entry.createdAt);
      return isDateInRange(created, startDate, endDate);
    });
  }, [winsExcited, startDate, endDate, selectedUserId]);

  return {
    summary: getSummary,
    trendData: getTrendData,
    activeRocks: getActiveRocks,
    completedItems: getCompletedItems,
    winsExcitedEntries: getWinsExcitedEntries,
    users: eosUsers,
    loading,
  };
}

